import { useEffect, useState, useCallback } from 'react';
import { Sparkles, Wallet, Check, Clock, XCircle, CheckCircle2, ExternalLink, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { LoadingState, UnauthorizedState } from '@/components/states';

const STATUS_LABEL = {
  CREATED: { label: 'Créée', cls: 'text-muted-foreground', icon: Clock },
  PENDING_VERIFICATION: { label: 'En cours de vérification', cls: 'text-[#F59E0B]', icon: Clock },
  CONFIRMED: { label: 'Confirmée', cls: 'text-[#22C55E]', icon: CheckCircle2 },
  REJECTED: { label: 'Rejetée', cls: 'text-destructive', icon: XCircle },
  CREDITED: { label: 'Créditée', cls: 'text-[#22C55E]', icon: CheckCircle2 },
};

const inp = 'w-full rounded-xl border border-border bg-surface-input px-4 py-2.5 text-white placeholder:text-muted-foreground focus:border-brand/60 focus:outline-none';

export default function Credits() {
  const { session, profile, refreshProfile } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null); // active order being paid
  const [step, setStep] = useState('offer'); // offer | pay | confirm | done
  const [form, setForm] = useState({ wave_payer_number: '', provider_transaction_id: '', declared_paid_at: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, o] = await Promise.all([api.get('/billing/config'), api.get('/billing/orders')]);
    setCfg(c.data); setOrders(o.data);
  }, []);

  useEffect(() => { if (session) load().finally(() => setLoading(false)); }, [session, load]);

  if (!session) return <UnauthorizedState />;
  if (loading) return <LoadingState />;

  const startPurchase = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/billing/orders');
      setOrder(data);
      setStep('pay');
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!form.wave_payer_number.trim()) {
      toast.error('Renseigne ton numéro Wave.'); return;
    }
    setBusy(true);
    try {
      await api.post(`/billing/orders/${order.id}/submit`, form);
      setStep('done');
      await load();
      await refreshProfile?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8" data-testid="credits-page">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
          <Wallet className="h-7 w-7 text-brand" /> Crédits
        </h1>
        <p className="mt-1 text-muted-foreground">Achète des générations pour créer et publier ta musique.</p>
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-border bg-surface-card p-5">
        <div>
          <p className="text-sm text-muted-foreground">Crédits disponibles</p>
          <p className="font-display text-3xl font-extrabold text-brand" data-testid="credits-balance">{profile?.credits_balance ?? 0}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Statut</p>
          <p className={`font-display text-lg font-bold ${profile?.is_premium ? 'text-[#22C55E]' : 'text-white'}`}>
            {profile?.is_premium ? 'Premium' : 'Essai gratuit'}
          </p>
        </div>
      </div>

      {/* Purchase flow */}
      <div className="card-surface overflow-hidden">
        <div className="border-b border-border bg-brand/5 p-6">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="font-display text-xl font-bold text-white">Pack MyMusic IA</p>
              <p className="text-sm text-muted-foreground">{cfg?.pack?.credits ?? ''} générations · crédits cumulables · sans expiration</p>
            </div>
            <p className="font-display text-3xl font-extrabold text-brand">1 000 <span className="text-base">FCFA</span></p>
          </div>
        </div>

        <div className="p-6">
          {step === 'offer' && (
            <button onClick={startPurchase} disabled={busy} data-testid="buy-pack-button"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3 font-semibold text-black btn-glow disabled:opacity-40">
              <Sparkles className="h-4 w-4" /> Acheter ce pack
            </button>
          )}

          {step === 'pay' && (
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-surface-input p-4 text-sm text-muted-foreground">
                Commande <span className="font-mono text-brand">{order?.reference}</span> — Montant à payer : <span className="font-semibold text-white">1 000 FCFA</span>
              </div>
              {cfg?.wave_configured ? (
                <div className="flex flex-col items-center gap-4">
                  <img src={cfg?.wave_qr_url || '/wave-merchant-qr.png'} alt="QR Code marchand Wave — scanne pour payer"
                       data-testid="wave-qr"
                       onError={(e) => { e.currentTarget.style.display = 'none'; }}
                       className="h-64 w-64 rounded-2xl border border-border bg-white object-contain p-3 sm:h-72 sm:w-72" />
                  <p className="-mt-1 text-center text-xs text-muted-foreground">Scanne ce QR avec l'app Wave, ou utilise le bouton ci-dessous sur mobile.</p>
                  <a href={cfg.wave_payment_url} target="_blank" rel="noreferrer" data-testid="wave-pay-link"
                     className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1DC8E9] py-3 font-semibold text-black btn-glow">
                    <ExternalLink className="h-4 w-4" /> Payer avec Wave
                  </a>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-4 text-sm text-[#F59E0B]" data-testid="wave-unconfigured">
                  <QrCode className="h-5 w-5" /> Le paiement Wave sera bientôt disponible. Tu peux tout de même déclarer un paiement effectué.
                </div>
              )}
              <button onClick={() => setStep('confirm')} data-testid="i-paid-button"
                      className="w-full rounded-full bg-surface-card py-3 font-semibold text-white hover:bg-surface-cardHover">
                J'ai effectué le paiement
              </button>
            </div>
          )}

          {step === 'confirm' && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Renseigne les informations de ton paiement Wave (montant attendu : 1 000 FCFA).</p>
              <input className={inp} placeholder="Numéro Wave utilisé (ex: +2250700000000)" data-testid="wave-number-input"
                     value={form.wave_payer_number} onChange={(e) => setForm({ ...form, wave_payer_number: e.target.value })} />
              <input className={inp} placeholder="ID de transaction Wave (facultatif)" data-testid="wave-txid-input"
                     value={form.provider_transaction_id} onChange={(e) => setForm({ ...form, provider_transaction_id: e.target.value })} />
              <label className="block text-xs text-muted-foreground">Date & heure approximatives du paiement
                <input type="datetime-local" className={inp} data-testid="wave-date-input"
                       value={form.declared_paid_at} onChange={(e) => setForm({ ...form, declared_paid_at: e.target.value })} />
              </label>
              <button onClick={submit} disabled={busy} data-testid="submit-payment-button"
                      className="w-full rounded-full bg-brand py-3 font-semibold text-black btn-glow disabled:opacity-40">
                {busy ? 'Envoi...' : 'Envoyer pour vérification'}
              </button>
            </div>
          )}

          {step === 'done' && (
            <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="payment-pending-message">
              <Clock className="h-10 w-10 text-[#F59E0B]" />
              <p className="font-display text-lg font-semibold text-white">Paiement en cours de vérification</p>
              <p className="max-w-md text-sm text-muted-foreground">Nous vérifions ton paiement Wave. Tes {cfg?.pack?.credits ?? ''} crédits seront ajoutés dès validation.</p>
              <button onClick={() => { setStep('offer'); setOrder(null); setForm({ wave_payer_number: '', provider_transaction_id: '', declared_paid_at: '' }); }}
                      className="mt-2 rounded-full bg-surface-card px-6 py-2.5 text-sm font-semibold text-white hover:bg-surface-cardHover">
                Terminé
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Orders history */}
      {orders.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-display text-xl font-semibold text-white">Mes commandes</h2>
          {orders.map((o) => {
            const st = STATUS_LABEL[o.status] || STATUS_LABEL.CREATED;
            const Icon = st.icon;
            return (
              <div key={o.id} className="card-surface flex items-center justify-between p-4" data-testid={`order-${o.reference}`}>
                <div>
                  <p className="font-mono text-sm text-brand">{o.reference}</p>
                  <p className="text-xs text-muted-foreground">{o.credits} crédits · {o.amount_fcfa} FCFA{o.provider_transaction_id ? ` · Tx ${o.provider_transaction_id}` : ''}</p>
                </div>
                <p className={`flex items-center gap-1.5 text-sm font-semibold ${st.cls}`}>
                  <Icon className="h-4 w-4" /> {st.label}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
